/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {
  trackingAssetUpdatesEnabledSelector,
  trackingFleetUpdateFlagSelector
} from '../../../store/selectors/tracking-selectors';

/**
 * The `useFleetUpdates` hook is designed to manage and respond to fleet tracking
 * updates efficiently in a React-Redux environment. This hook is especially useful
 * in situations where your application needs to perform a refresh or trigger a
 * side effect whenever specific updates to the fleet tracking state occur.
 * It integrates seamlessly with your Redux store and allows you to define
 * custom logic that should be executed when certain conditions are met,
 * such as when a fleet update flag is set.
 *
 * ```typescript
 * 
 *  import {useFleetUpdates} from './useFleetUpdates';
 * 
 *  const refreshFleetData = async () => {
 *    await dispatch(fetchFleetData());
 *  };
 * 
 *  useFleetUpdates(refreshFleetData, isLoading);
 * 
 * ```
 *
 * @param refreshFn - The function to call when a fleet update is detected.
 * @param loading - Optional flag indicating if the component is in a loading state. Defaults to `false`.
 */
export const useFleetUpdates = (
  refreshFn: Function,
  loading: boolean = false
): void => {
  const fleetUpdateFlag       = useSelector(trackingFleetUpdateFlagSelector);
  const isAssetUpdatesEnabled = useSelector(trackingAssetUpdatesEnabledSelector);

  const refreshFnRef = useRef(refreshFn);

  useEffect(() => {
    refreshFnRef.current = refreshFn;
  }, [refreshFn]);
  
  useEffect(() => {
    if (!loading && fleetUpdateFlag && isAssetUpdatesEnabled) {
      refreshFnRef.current();
    }
  }, [fleetUpdateFlag, isAssetUpdatesEnabled]);
};

export default useFleetUpdates;
